import { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const G = "#C9A84C";
const BG = "#06060F";
const SFC = "#0E0E1C";
const SFC2 = "#14142A";
const BDR = "rgba(255,255,255,0.07)";
const TXT = "#F0F0FF";
const MUT = "#8888BB";
const RED = "#FF6B6B";
const GRN = "#4ADE80";

const CATS = {
  expense: [
    { id: "food", label: "Food", emoji: "🍔", color: "#FF6B6B" },
    { id: "transport", label: "Transport", emoji: "🚌", color: "#60A5FA" },
    { id: "bills", label: "Bills", emoji: "💡", color: "#FBBF24" },
    { id: "shopping", label: "Shopping", emoji: "🛍️", color: "#A78BFA" },
    { id: "education", label: "Education", emoji: "📚", color: "#34D399" },
    { id: "health", label: "Health", emoji: "💊", color: "#6EE7B7" },
    { id: "entertainment", label: "Entertainment", emoji: "🎮", color: "#F472B6" },
    { id: "other", label: "Other", emoji: "📦", color: "#94A3B8" },
  ],
  income: [
    { id: "salary", label: "Salary", emoji: "💼", color: "#4ADE80" },
    { id: "freelance", label: "Freelance", emoji: "💻", color: "#60A5FA" },
    { id: "investment", label: "Investment", emoji: "📈", color: "#C9A84C" },
    { id: "gift", label: "Gift", emoji: "🎁", color: "#F472B6" },
    { id: "other", label: "Other", emoji: "💰", color: "#94A3B8" },
  ],
};

const WCOLORS = ["#C9A84C", "#8B5CF6", "#10B981", "#3B82F6", "#EF4444", "#EC4899"];
const LS_KEYS = {
  users: "mp_users",
  session: "mp_session",
  data: "mp_user_data",
};

const lkr = (n) => "LKR " + Number(n).toLocaleString("en-LK");
const fmtD = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
const getCat = (type, id) => {
  const pool = CATS[type] || [...CATS.expense, ...CATS.income];
  return pool.find((c) => c.id === id) || { label: id, emoji: "📦", color: "#94A3B8" };
};
const wico = (t) => (t === "cash" ? "💵" : t === "card" ? "💳" : "🏦");

const Btn = ({ children, onClick, style = {}, disabled = false, variant = "primary" }) => {
  const base = {
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    borderRadius: 14,
    padding: "14px 24px",
    fontWeight: 600,
    fontSize: 15,
    transition: "all 0.2s",
    fontFamily: "inherit",
    opacity: disabled ? 0.45 : 1,
  };
  const vars = {
    primary: { background: `linear-gradient(135deg,${G},#E8C96C)`, color: "#000" },
    ghost: { background: "transparent", color: TXT, border: `1px solid ${BDR}` },
    danger: { background: "rgba(255,107,107,0.12)", color: RED, border: `1px solid rgba(255,107,107,0.25)` },
  };
  return (
    <button onClick={disabled ? undefined : onClick} style={{ ...base, ...vars[variant], ...style }}>
      {children}
    </button>
  );
};

const Input = ({ label, value, onChange, type = "text", placeholder = "", style = {} }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <div style={{ color: MUT, fontSize: 13, marginBottom: 6, fontWeight: 500 }}>{label}</div>}
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%",
        background: SFC2,
        border: `1px solid ${BDR}`,
        borderRadius: 12,
        padding: "13px 16px",
        color: TXT,
        fontSize: 15,
        fontFamily: "inherit",
        outline: "none",
        boxSizing: "border-box",
        ...style,
      }}
    />
  </div>
);

const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: SFC,
          borderRadius: "24px 24px 0 0",
          width: "100%",
          maxWidth: 430,
          maxHeight: "92vh",
          overflowY: "auto",
          padding: "24px 24px 44px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: TXT }}>{title}</div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "none",
              cursor: "pointer",
              borderRadius: 10,
              width: 36,
              height: 36,
              fontSize: 20,
              color: MUT,
              lineHeight: "36px",
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

function TxnIcon({ type, cat, billImage, billImages, size = 44 }) {
  const [imgError, setImgError] = useState(false);
  const c = getCat(type, cat);
  const src = billImages?.[0] || billImage;

  if (type === "expense" && src && !imgError) {
    return (
      <img
        src={src}
        alt="Bill"
        onError={() => setImgError(true)}
        style={{ width: size, height: size, borderRadius: 14, objectFit: "cover", border: `1px solid ${BDR}`, flexShrink: 0 }}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 14,
        fontSize: size === 44 ? 20 : 22,
        background: `${c.color}22`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {c.emoji}
    </div>
  );
}

function Dashboard({ wallets, txns, setModal }) {
  const total = wallets.reduce((s, w) => s + w.balance, 0);
  const cash = wallets.filter((w) => w.type === "cash").reduce((s, w) => s + w.balance, 0);
  const cards = wallets.filter((w) => w.type !== "cash").reduce((s, w) => s + w.balance, 0);
  const now = new Date();
  const monthExp = txns
    .filter((t) => {
      const d = new Date(t.date);
      return t.type === "expense" && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((s, t) => s + t.amount, 0);
  const recent = [...txns].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6);

  return (
    <div style={{ padding: "80px 18px 100px", overflowY: "auto", height: "100vh" }}>


      <div
        style={{
          background: "linear-gradient(135deg,#1A1430,#0D1A2E)",
          border: "1px solid rgba(201,168,76,0.2)",
          borderRadius: 24,
          padding: 24,
          marginBottom: 16,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "radial-gradient(circle,rgba(201,168,76,0.12),transparent)",
            pointerEvents: "none",
          }}
        />
        <div style={{ color: MUT, fontSize: 13, marginBottom: 6 }}>Total Balance</div>
        <div style={{ color: G, fontSize: 36, fontWeight: 800, letterSpacing: -1, marginBottom: 16 }}>{lkr(total)}</div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {[
            { label: "💵 Cash", val: lkr(cash), col: "#C9A84C" },
            { label: "💳 Cards & Banks", val: lkr(cards), col: TXT },
            { label: "📉 This Month", val: "-" + lkr(monthExp), col: RED },
          ].map((s) => (
            <div key={s.label}>
              <div style={{ color: MUT, fontSize: 11, marginBottom: 2 }}>{s.label}</div>
              <div style={{ color: s.col, fontSize: 14, fontWeight: 700 }}>{s.val}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Income", icon: "↑", col: GRN, rgb: "74,222,128", action: () => setModal({ type: "txn", txnType: "income" }) },
          { label: "Expense", icon: "↓", col: RED, rgb: "255,107,107", action: () => setModal({ type: "txn", txnType: "expense" }) },
          { label: "Transfer", icon: "⇄", col: G, rgb: "201,168,76", action: () => setModal({ type: "transfer" }) },
        ].map((a) => (
          <button
            key={a.label}
            onClick={a.action}
            style={{
              flex: 1,
              background: SFC,
              border: `1px solid ${BDR}`,
              borderRadius: 16,
              padding: "14px 6px",
              cursor: "pointer",
              fontFamily: "inherit",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                background: `rgba(${a.rgb},0.12)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                color: a.col,
                fontWeight: 700,
              }}
            >
              {a.icon}
            </div>
            <div style={{ color: TXT, fontSize: 12, fontWeight: 500 }}>{a.label}</div>
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ color: TXT, fontSize: 16, fontWeight: 700, marginBottom: 12 }}>My Wallets</div>
        <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 6, WebkitOverflowScrolling: "touch" }}>
          {wallets.map((w) => (
            <div
              key={w.id}
              style={{
                minWidth: 160,
                background: `linear-gradient(135deg,${w.color}1A,${w.color}0D)`,
                border: `1px solid ${w.color}33`,
                borderRadius: 18,
                padding: 16,
                flexShrink: 0,
              }}
            >
              <div style={{ fontSize: 22, marginBottom: 8 }}>{wico(w.type)}</div>
              <div style={{ color: TXT, fontSize: 13, fontWeight: 500, marginBottom: 4, opacity: 0.85 }}>{w.name}</div>
              <div style={{ color: w.color, fontSize: 17, fontWeight: 800 }}>{lkr(w.balance)}</div>
              {w.bankName && <div style={{ color: MUT, fontSize: 10, marginTop: 2 }}>{w.bankName}</div>}
            </div>
          ))}
          <button
            onClick={() => setModal({ type: "addWallet" })}
            style={{
              minWidth: 100,
              background: "transparent",
              border: `1px dashed ${BDR}`,
              borderRadius: 18,
              padding: 16,
              flexShrink: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <div style={{ fontSize: 24, color: MUT }}>+</div>
            <div style={{ color: MUT, fontSize: 11 }}>Add Wallet</div>
          </button>
        </div>
      </div>

      <div>
        <div style={{ color: TXT, fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Recent Transactions</div>
        {recent.map((t) => {
          const w = wallets.find((x) => x.id === t.wId) || { name: "?", color: "#94A3B8" };
          const c = getCat(t.type, t.cat);
          return (
            <div
              key={t.id}
              onClick={() => setModal({ type: "txn", editData: t })}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 10px",
                borderBottom: `1px solid ${BDR}`,
                cursor: "pointer",
                borderRadius: 12,
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.03)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <TxnIcon type={t.type} cat={t.cat} billImage={t.billImage} billImages={t.billImages} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: TXT, fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t.note || c.label}
                </div>
                <div style={{ color: MUT, fontSize: 11, marginTop: 2 }}>{w.name} · {fmtD(t.date)}</div>
              </div>
              <div style={{ color: t.type === "income" ? GRN : RED, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                {t.type === "income" ? "+" : "-"}
                {lkr(t.amount)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Wallets({ wallets, txns, setModal, onDeleteWallet }) {
  const total = wallets.reduce((s, w) => s + w.balance, 0);
  return (
    <div style={{ padding: "80px 18px 100px", overflowY: "auto", height: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ color: TXT, fontSize: 22, fontWeight: 800 }}>Wallets</div>
        <Btn onClick={() => setModal({ type: "addWallet" })} style={{ padding: "10px 16px", fontSize: 13 }}>
          + Add Wallet
        </Btn>
      </div>

      <div
        style={{
          background: "linear-gradient(135deg,rgba(201,168,76,0.12),rgba(201,168,76,0.04))",
          border: "1px solid rgba(201,168,76,0.2)",
          borderRadius: 20,
          padding: 20,
          marginBottom: 20,
        }}
      >
        <div style={{ color: MUT, fontSize: 13 }}>Total Net Worth</div>
        <div style={{ color: G, fontSize: 30, fontWeight: 800, marginTop: 4 }}>{lkr(total)}</div>
        <div style={{ color: MUT, fontSize: 12, marginTop: 4 }}>
          {wallets.length} wallet{wallets.length !== 1 ? "s" : ""} connected
        </div>
      </div>

      {wallets.map((w) => {
        const wTxns = txns.filter((t) => t.wId === w.id);
        const wInc = wTxns.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
        const wExp = wTxns.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
        return (
          <div key={w.id} style={{ background: SFC, border: `1px solid ${BDR}`, borderRadius: 20, padding: 20, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 15,
                    background: `${w.color}22`,
                    border: `1px solid ${w.color}44`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 24,
                  }}
                >
                  {wico(w.type)}
                </div>
                <div>
                  <div style={{ color: TXT, fontSize: 16, fontWeight: 700 }}>{w.name}</div>
                  {w.bankName && <div style={{ color: MUT, fontSize: 12, marginTop: 2 }}>{w.bankName}</div>}
                  <div style={{ color: MUT, fontSize: 11, marginTop: 1, textTransform: "capitalize" }}>{w.type}</div>
                </div>
              </div>
              <button
                onClick={() => onDeleteWallet(w.id)}
                style={{ background: "rgba(255,107,107,0.1)", border: "none", cursor: "pointer", borderRadius: 10, width: 34, height: 34, fontSize: 15, color: RED }}
              >
                🗑
              </button>
            </div>
            <div style={{ color: w.color, fontSize: 26, fontWeight: 800, marginBottom: 14 }}>{lkr(w.balance)}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1, background: `${GRN}0F`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ color: MUT, fontSize: 11, marginBottom: 2 }}>💹 Income</div>
                <div style={{ color: GRN, fontSize: 13, fontWeight: 700 }}>+{lkr(wInc)}</div>
              </div>
              <div style={{ flex: 1, background: `${RED}0F`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ color: MUT, fontSize: 11, marginBottom: 2 }}>📉 Expenses</div>
                <div style={{ color: RED, fontSize: 13, fontWeight: 700 }}>-{lkr(wExp)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function History({ wallets, txns, onDelete, setModal }) {
  const [filter, setFilter] = useState("all");
  const sorted = [...txns].sort((a, b) => new Date(b.date) - new Date(a.date));
  const filtered = filter === "all" ? sorted : sorted.filter((t) => t.type === filter);

  return (
    <div style={{ padding: "80px 18px 100px", overflowY: "auto", height: "100vh" }}>
      <div style={{ color: TXT, fontSize: 22, fontWeight: 800, marginBottom: 16 }}>History</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 18, overflowX: "auto", paddingBottom: 4, WebkitOverflowScrolling: "touch" }}>
        {["all", "income", "expense", "transfer"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "8px 16px",
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: "nowrap",
              background: filter === f ? G : "rgba(255,255,255,0.06)",
              color: filter === f ? "#000" : MUT,
              transition: "all 0.2s",
              textTransform: "capitalize",
            }}
          >
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", color: MUT, padding: "50px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div>No transactions found</div>
        </div>
      ) : (
        filtered.map((t) => {
          const w = wallets.find((x) => x.id === t.wId) || { name: "Unknown", color: "#94A3B8" };
          const c = getCat(t.type, t.cat);
          return (
            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, background: SFC, borderRadius: 16, padding: "14px", marginBottom: 8 }}>
              <TxnIcon type={t.type} cat={t.cat} billImage={t.billImage} billImages={t.billImages} size={46} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: TXT, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t.note || c.label}
                </div>
                <div style={{ color: MUT, fontSize: 11, marginTop: 3 }}>
                  {c.label} · {w.name} · {fmtD(t.date)}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ color: t.type === "income" ? GRN : RED, fontSize: 14, fontWeight: 700 }}>
                  {t.type === "income" ? "+" : "-"}
                  {lkr(t.amount)}
                </div>
                {t.type !== "transfer" && (
                  <button
                    onClick={() => setModal({ type: "txn", editData: t })}
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "none",
                      borderRadius: 8,
                      padding: "4px 10px",
                      color: G,
                      fontSize: 11,
                      fontWeight: 600,
                      marginTop: 6,
                      cursor: "pointer",
                    }}
                  >
                    {t.type === "expense" ? "View" : "Edit"}
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function Reports({ txns }) {
  const now = new Date();
  const thisM = txns.filter((t) => {
    const d = new Date(t.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const income = thisM.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const expense = thisM.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
  const savings = income - expense;

  const catMap = {};
  txns
    .filter((t) => t.type === "expense")
    .forEach((t) => {
      catMap[t.cat] = (catMap[t.cat] || 0) + t.amount;
    });
  const pieData = Object.entries(catMap)
    .map(([id, value]) => {
      const c = getCat("expense", id);
      return { name: c.label, value, color: c.color, emoji: c.emoji };
    })
    .sort((a, b) => b.value - a.value);
  const pieTotal = pieData.reduce((s, x) => s + x.value, 0);

  const barData = Array.from({ length: 6 }).map((_, i) => {
    const d = new Date(now);
    d.setMonth(d.getMonth() - (5 - i));
    const label = d.toLocaleDateString("en-GB", { month: "short" });
    const amt = txns
      .filter((t) => {
        const td = new Date(t.date);
        return t.type === "expense" && td.getMonth() === d.getMonth() && td.getFullYear() === d.getFullYear();
      })
      .reduce((s, t) => s + t.amount, 0);
    return { month: label, amount: amt };
  });

  return (
    <div style={{ padding: "80px 18px 100px", overflowY: "auto", height: "100vh" }}>
      <div style={{ color: TXT, fontSize: 22, fontWeight: 800, marginBottom: 16 }}>Reports</div>
      <div style={{ color: MUT, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>This Month</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Income", val: income, col: GRN, pre: "+" },
          { label: "Expenses", val: expense, col: RED, pre: "-" },
          { label: "Savings", val: Math.abs(savings), col: savings >= 0 ? G : RED, pre: savings >= 0 ? "+" : "-" },
        ].map((s) => (
          <div key={s.label} style={{ background: SFC, border: `1px solid ${BDR}`, borderRadius: 16, padding: "14px 12px" }}>
            <div style={{ color: MUT, fontSize: 10, marginBottom: 6, fontWeight: 600 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.col, fontSize: 12, fontWeight: 800, lineHeight: 1.3 }}>
              {s.pre}
              {lkr(s.val)}
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: SFC, border: `1px solid ${BDR}`, borderRadius: 20, padding: 20, marginBottom: 16 }}>
        <div style={{ color: TXT, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Monthly Spending</div>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={barData} margin={{ top: 0, right: 0, bottom: 0, left: -22 }}>
            <XAxis dataKey="month" tick={{ fill: MUT, fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: MUT, fontSize: 9 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: SFC2, border: `1px solid ${BDR}`, borderRadius: 10, color: TXT, fontSize: 12 }}
              formatter={(v) => [lkr(v), "Spent"]}
              cursor={{ fill: "rgba(255,255,255,0.04)" }}
            />
            <Bar dataKey="amount" fill={G} radius={[6, 6, 0, 0]} opacity={0.85} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ background: SFC, border: `1px solid ${BDR}`, borderRadius: 20, padding: 20 }}>
        <div style={{ color: TXT, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Expense Breakdown</div>
        {pieData.length > 0 ? (
          <>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
              <PieChart width={200} height={160}>
                <Pie data={pieData} cx={100} cy={80} innerRadius={44} outerRadius={74} dataKey="value" paddingAngle={2}>
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: SFC2, border: `1px solid ${BDR}`, borderRadius: 10, color: TXT, fontSize: 12 }} formatter={(v) => [lkr(v), "Spent"]} />
              </PieChart>
            </div>
            {pieData.map((item) => {
              const pct = pieTotal ? Math.round((item.value / pieTotal) * 100) : 0;
              return (
                <div key={item.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${BDR}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 14 }}>{item.emoji}</span>
                    <div style={{ color: TXT, fontSize: 13 }}>{item.name}</div>
                    <div style={{ color: MUT, fontSize: 11 }}>{pct}%</div>
                  </div>
                  <div style={{ color: RED, fontSize: 13, fontWeight: 700 }}>-{lkr(item.value)}</div>
                </div>
              );
            })}
          </>
        ) : (
          <div style={{ textAlign: "center", color: MUT, padding: "20px 0" }}>No expense data yet</div>
        )}
      </div>
    </div>
  );
}

function BigImage({ src, idx, total, date, onRemove, canRemove }) {
  const [error, setError] = useState(false);
  if (error) {
    return (
      <div style={{ width: "100%", height: total === 1 ? 250 : 140, borderRadius: 16, border: `1px solid ${BDR}`, background: SFC2, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}>
        <div style={{ fontSize: 32 }}>⚠️</div>
        <div style={{ color: MUT, fontSize: 10 }}>Image too large to load</div>
      </div>
    );
  }
  return (
    <div style={{ position: "relative", width: "100%", background: "#000", borderRadius: 16, border: `1px solid ${BDR}`, overflow: "hidden" }}>
      <img
        src={src}
        alt={`Receipt ${idx + 1}`}
        onError={() => setError(true)}
        style={{ width: "100%", height: total === 1 ? 250 : 140, objectFit: "cover", display: "block" }}
      />
      {canRemove && (
        <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6 }}>
          <a
            href={src}
            download={`receipt-${date}-${idx + 1}.png`}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)", color: "white", textDecoration: "none", borderRadius: "50%", width: 26, height: 26, cursor: "pointer" }}
            title="Download Image"
          >
            ↓
          </a>
          <button
            onClick={() => onRemove(idx)}
            style={{ background: "rgba(255,107,107,0.9)", color: "white", border: "none", borderRadius: "50%", width: 26, height: 26, cursor: "pointer", fontWeight: "bold" }}
            title="Remove Image"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function AddTxnModal({ show, onClose, wallets, onSave, initType = "expense", editData = null, onDelete }) {
  const [txnType, setTxnType] = useState(initType);
  const [amount, setAmount] = useState("");
  const [cat, setCat] = useState("");
  const [wId, setWId] = useState("");
  const [note, setNote] = useState("");
  const [billImages, setBillImages] = useState([]); // Changed to array
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    if (show) {
      if (editData) {
        setTxnType(editData.type);
        setAmount(editData.amount.toString());
        setCat(editData.cat);
        setWId(editData.wId);
        setNote(editData.note || "");
        // Support legacy single image or new array format
        setBillImages(Array.isArray(editData.billImages) ? editData.billImages : (editData.billImage ? [editData.billImage] : []));
        setDate(editData.date.split("T")[0]);
      } else {
        setTxnType(initType);
        setAmount("");
        setCat("");
        setNote("");
        setBillImages([]);
        setWId(wallets[0]?.id || "");
        setDate(new Date().toISOString().split("T")[0]);
      }
    }
  }, [show, initType, wallets, editData]);

  const [isUploading, setIsUploading] = useState(false);

  const handleBillUpload = (files) => {
    if (!files || files.length === 0) return;
    const availableSlots = 5 - billImages.length;
    if (availableSlots <= 0) return;
    const validFiles = Array.from(files).slice(0, availableSlots);

    setIsUploading(true);
    Promise.all(validFiles.map(file => {
      return new Promise((resolve) => {
        // Use createObjectURL for reliable mobile loading (avoids FileReader base64 issues)
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          const max = 1200;
          if (w > h && w > max) { h = Math.round(h * max / w); w = max; }
          else if (h > max) { w = Math.round(w * max / h); h = max; }
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          URL.revokeObjectURL(objectUrl);
          resolve(canvas.toDataURL("image/jpeg", 0.75));
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          // Fallback: try FileReader base64 approach
          const reader = new FileReader();
          reader.onload = (e) => {
            const img2 = new Image();
            img2.onload = () => {
              const canvas = document.createElement("canvas");
              let w = img2.width, h = img2.height;
              const max = 1200;
              if (w > h && w > max) { h = Math.round(h * max / w); w = max; }
              else if (h > max) { w = Math.round(w * max / h); h = max; }
              canvas.width = w; canvas.height = h;
              canvas.getContext("2d").drawImage(img2, 0, 0, w, h);
              resolve(canvas.toDataURL("image/jpeg", 0.75));
            };
            img2.onerror = () => resolve(null);
            img2.src = String(e.target.result);
          };
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(file);
        };
        img.src = objectUrl;
      });
    })).then(newImages => {
      const filtered = newImages.filter(x => x !== null);
      setBillImages(prev => [...prev, ...filtered]);
      setIsUploading(false);
    }).catch(() => setIsUploading(false));
  };

  const removeImage = (index) => {
    setBillImages(prev => prev.filter((_, i) => i !== index));
  };

  const submit = () => {
    if (!amount || !cat || !wId) return;
    onSave(
      {
        id: editData ? editData.id : uid(),
        wId,
        type: txnType,
        amount: parseFloat(amount),
        cat,
        note,
        billImages: txnType === "expense" ? billImages : [],
        date: new Date(date).toISOString(),
      },
      !!editData
    );
    onClose();
  };

  return (
    <Modal show={show} onClose={onClose} title={editData ? "Transaction Details" : "Add Transaction"}>
      {txnType === "expense" && billImages.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: billImages.length === 1 ? "1fr" : "1fr 1fr",
            gap: 10
          }}>
            {billImages.map((imgSrc, idx) => (
              <BigImage
                key={idx}
                src={imgSrc}
                idx={idx}
                total={billImages.length}
                date={date}
                onRemove={removeImage}
                canRemove={true}
              />
            ))}
          </div>
          <div style={{ color: MUT, fontSize: 11, textAlign: "center", marginTop: 8 }}>
            {billImages.length} Receipt{billImages.length !== 1 ? "s" : ""} Attached
          </div>
        </div>
      )}

      {!editData && (
        <div style={{ display: "flex", background: SFC2, borderRadius: 14, padding: 4, marginBottom: 20 }}>
          {["expense", "income"].map((t) => (
            <button
              key={t}
              onClick={() => setTxnType(t)}
              style={{
                flex: 1,
                padding: "10px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                background: txnType === t ? (t === "income" ? GRN : RED) : "transparent",
                color: txnType === t ? "#000" : MUT,
                fontWeight: 600,
                fontSize: 14,
                transition: "all 0.2s",
                textTransform: "capitalize",
              }}
            >
              {t}
            </button>
          ))}
        </div>
      )}
      <div style={{ background: SFC2, borderRadius: 18, padding: "20px 16px", textAlign: "center", marginBottom: 18, border: `1px solid ${BDR}` }}>
        <div style={{ color: MUT, fontSize: 12, marginBottom: 6 }}>Amount (LKR)</div>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            fontSize: 34,
            fontWeight: 800,
            color: txnType === "income" ? GRN : RED,
            textAlign: "center",
            width: "100%",
            fontFamily: "inherit",
          }}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <div style={{ color: MUT, fontSize: 13, marginBottom: 10, fontWeight: 500 }}>Category</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
          {(CATS[txnType] || []).map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              style={{
                padding: "10px 4px",
                borderRadius: 12,
                border: `1px solid ${cat === c.id ? c.color : BDR}`,
                background: cat === c.id ? `${c.color}22` : "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
              }}
            >
              <div style={{ fontSize: 20 }}>{c.emoji}</div>
              <div style={{ color: cat === c.id ? c.color : MUT, fontSize: 9, fontWeight: 600 }}>{c.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ color: MUT, fontSize: 13, marginBottom: 8, fontWeight: 500 }}>Wallet</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {wallets.map((w) => (
            <button
              key={w.id}
              onClick={() => setWId(w.id)}
              style={{
                padding: "8px 14px",
                borderRadius: 12,
                border: `1px solid ${wId === w.id ? w.color : BDR}`,
                background: wId === w.id ? `${w.color}22` : "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                color: wId === w.id ? w.color : MUT,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {wico(w.type)} {w.name}
            </button>
          ))}
        </div>
      </div>

      <Input label="Note (optional)" value={note} onChange={setNote} placeholder="What was this for?" />
      {txnType === "expense" && (
        <div style={{ marginBottom: 16 }}>
          {billImages.length < 5 ? (
            <>
              <div style={{ color: MUT, fontSize: 13, marginBottom: 8, fontWeight: 500 }}>
                {billImages.length > 0 ? `Add more receipts (can add ${5 - billImages.length} more)` : "Attach Receipts (up to 5)"}
              </div>
              {isUploading ? (
                <div style={{ background: SFC2, border: `1px solid ${BDR}`, borderRadius: 12, padding: "16px", color: G, fontSize: 13, textAlign: "center", fontWeight: 700 }}>
                  ⏳ Processing & Compressing...
                </div>
              ) : (
                <div style={{ display: "flex", gap: 10 }}>
                  <label style={{ flex: 1 }}>
                    <div style={{ background: SFC2, border: `1px solid ${BDR}`, borderRadius: 12, padding: "12px", color: TXT, fontSize: 13, textAlign: "center", cursor: "pointer", fontWeight: 600 }}>
                      📸 Take Photo
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => { handleBillUpload(e.target.files); e.target.value = ""; }}
                      style={{ display: "none" }}
                    />
                  </label>
                  <label style={{ flex: 1 }}>
                    <div style={{ background: SFC2, border: `1px solid ${BDR}`, borderRadius: 12, padding: "12px", color: TXT, fontSize: 13, textAlign: "center", cursor: "pointer", fontWeight: 600 }}>
                      🖼️ Gallery
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => { handleBillUpload(e.target.files); e.target.value = ""; }}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>
              )}
            </>
          ) : (
            <div style={{ color: G, fontSize: 13, textAlign: "center", padding: "10px", background: "rgba(201,168,76,0.1)", borderRadius: 12 }}>
              Maximum 5 receipts attached.
            </div>
          )}
        </div>
      )}
      <Input label="Date" value={date} onChange={setDate} type="date" />
      <div style={{ display: "flex", gap: 10 }}>
        {editData && (
          <Btn onClick={() => onDelete(editData.id)} variant="danger" style={{ flex: 1 }}>
            Delete
          </Btn>
        )}
        <Btn onClick={submit} disabled={!amount || !cat || !wId} style={{ flex: 2 }}>
          {editData ? "Update Transaction" : "Save Transaction"}
        </Btn>
      </div>
    </Modal>
  );
}

function AddWalletModal({ show, onClose, onSave }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("cash");
  const [bankName, setBankName] = useState("");
  const [balance, setBalance] = useState("");
  const [color, setColor] = useState(WCOLORS[0]);

  useEffect(() => {
    if (show) {
      setName("");
      setType("cash");
      setBankName("");
      setBalance("");
      setColor(WCOLORS[0]);
    }
  }, [show]);

  const submit = () => {
    if (!name) return;
    onSave({ id: uid(), name, type, bankName, balance: parseFloat(balance) || 0, color });
    onClose();
  };

  return (
    <Modal show={show} onClose={onClose} title="Add Wallet">
      <Input label="Wallet Name" value={name} onChange={setName} placeholder="e.g. My Savings" />
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: MUT, fontSize: 13, marginBottom: 8, fontWeight: 500 }}>Type</div>
        <div style={{ display: "flex", gap: 8 }}>
          {[{ v: "cash", l: "💵 Cash" }, { v: "card", l: "💳 Card" }, { v: "bank", l: "🏦 Bank" }].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setType(opt.v)}
              style={{
                flex: 1,
                padding: "10px 6px",
                borderRadius: 12,
                border: `1px solid ${type === opt.v ? G : BDR}`,
                background: type === opt.v ? `${G}22` : "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                color: type === opt.v ? G : MUT,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>
      {type !== "cash" && <Input label="Bank Name" value={bankName} onChange={setBankName} placeholder="e.g. Bank of Ceylon" />}
      <Input label="Initial Balance (LKR)" value={balance} onChange={setBalance} type="number" placeholder="0.00" />
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: MUT, fontSize: 13, marginBottom: 8, fontWeight: 500 }}>Color</div>
        <div style={{ display: "flex", gap: 10 }}>
          {WCOLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: c,
                border: "none",
                cursor: "pointer",
                outline: color === c ? "2.5px solid white" : "2px solid transparent",
                outlineOffset: 2,
                transition: "all 0.2s",
              }}
            />
          ))}
        </div>
      </div>
      <Btn onClick={submit} disabled={!name} style={{ width: "100%" }}>
        Add Wallet
      </Btn>
    </Modal>
  );
}

function TransferModal({ show, onClose, wallets, onSave }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (show) {
      setFrom(wallets[0]?.id || "");
      setTo(wallets[1]?.id || "");
      setAmount("");
    }
  }, [show, wallets]);

  const submit = () => {
    if (!from || !to || !amount || from === to) return;
    onSave({ fromId: from, toId: to, amount: parseFloat(amount) });
    onClose();
  };

  const WPicker = ({ label, val, onChange, exclude }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: MUT, fontSize: 13, marginBottom: 8, fontWeight: 500 }}>{label}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {wallets
          .filter((w) => w.id !== exclude)
          .map((w) => (
            <button
              key={w.id}
              onClick={() => onChange(w.id)}
              style={{
                padding: "8px 14px",
                borderRadius: 12,
                border: `1px solid ${val === w.id ? w.color : BDR}`,
                background: val === w.id ? `${w.color}22` : "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                color: val === w.id ? w.color : MUT,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {wico(w.type)} {w.name}
            </button>
          ))}
      </div>
    </div>
  );

  return (
    <Modal show={show} onClose={onClose} title="Transfer Money">
      <WPicker label="From Wallet" val={from} onChange={setFrom} exclude={to} />
      <div style={{ textAlign: "center", color: G, fontSize: 26, marginBottom: 4 }}>↓</div>
      <WPicker label="To Wallet" val={to} onChange={setTo} exclude={from} />
      <div style={{ background: SFC2, borderRadius: 18, padding: "18px 16px", textAlign: "center", marginBottom: 20, border: `1px solid ${BDR}` }}>
        <div style={{ color: MUT, fontSize: 12, marginBottom: 6 }}>Transfer Amount (LKR)</div>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          style={{ background: "transparent", border: "none", outline: "none", fontSize: 34, fontWeight: 800, color: G, textAlign: "center", width: "100%", fontFamily: "inherit" }}
        />
      </div>
      <Btn onClick={submit} disabled={!from || !to || !amount || from === to} style={{ width: "100%" }}>
        Transfer Now
      </Btn>
    </Modal>
  );
}

function AddChoiceSheet({ show, onClose, onChoose }) {
  if (!show) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: SFC, borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 430, padding: "24px 20px 44px" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ color: TXT, fontSize: 18, fontWeight: 700, marginBottom: 20, textAlign: "center" }}>What would you like to do?</div>
        {[
          { label: "💰 Add Income", sub: "Record money received", type: "income", col: GRN },
          { label: "💸 Add Expense", sub: "Record money spent", type: "expense", col: RED },
          { label: "🔄 Transfer Money", sub: "Move between wallets", type: "transfer", col: G },
        ].map((o) => (
          <button key={o.type} onClick={() => onChoose(o.type)} style={{ display: "block", width: "100%", background: SFC2, border: `1px solid ${o.col}22`, borderRadius: 16, padding: "16px", textAlign: "left", cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
            <div style={{ color: TXT, fontSize: 15, fontWeight: 600 }}>{o.label}</div>
            <div style={{ color: MUT, fontSize: 12, marginTop: 3 }}>{o.sub}</div>
          </button>
        ))}
        <button onClick={onClose} style={{ display: "block", width: "100%", marginTop: 6, padding: "14px", borderRadius: 14, background: "transparent", border: `1px solid ${BDR}`, cursor: "pointer", color: MUT, fontSize: 14, fontFamily: "inherit" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function BottomNav({ screen, setScreen, setModal }) {
  const items = [
    { id: "dashboard", icon: "🏠", label: "Home" },
    { id: "wallets", icon: "💳", label: "Wallets" },
    { id: "add", isAdd: true },
    { id: "history", icon: "📋", label: "History" },
    { id: "reports", icon: "📊", label: "Reports" },
  ];
  return (
    <>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: 430,
          background: "rgba(6,6,15,0.96)",
          borderTop: `1px solid ${BDR}`,
          backdropFilter: "blur(20px)",
          zIndex: 90,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", height: 65, padding: "0 8px" }}>
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.isAdd) setModal({ type: "addChoice" });
                else setScreen(item.id);
              }}
              style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", padding: "8px 0" }}
            >
              {item.isAdd ? (
                <div style={{ width: 54, height: 54, borderRadius: 19, background: `linear-gradient(135deg,${G},#E8C96C)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, color: "#000", fontWeight: 700, marginTop: -22, boxShadow: "0 4px 24px rgba(201,168,76,0.45)" }}>+</div>
              ) : (
                <>
                  <div style={{ fontSize: 20, opacity: screen === item.id ? 1 : 0.35, transition: "all 0.2s" }}>{item.icon}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, transition: "all 0.2s", color: screen === item.id ? G : MUT }}>{item.label}</div>
                </>
              )}
            </button>
          ))}
        </div>
        <div style={{ textAlign: "center", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))", marginTop: -4 }}>
          <div style={{ color: TXT, fontSize: 12, marginTop: 2, opacity: 0.6 }}>by Ravi Praveen</div>
        </div>
      </div>
    </>
  );
}

function InstallBanner() {
  const [show, setShow] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Don't show if already installed (standalone mode)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (isStandalone) return;
    // Don't show if user already dismissed - removed as per request to keep showing until install

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIos(ios);

    if (ios) {
      // Show iOS instructions after 2s
      setTimeout(() => setShow(true), 2000);
    } else {
      // Listen for Android/Chrome install prompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setShow(true), 2000);
      };
      window.addEventListener("beforeinstallprompt", handler);
      return () => window.removeEventListener("beforeinstallprompt", handler);
    }
  }, []);

  const dismiss = () => {
    setShow(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setInstalling(false);
    if (outcome === "accepted") {
      setShow(false);
    }
    setDeferredPrompt(null);
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 90,
        left: "50%",
        transform: "translateX(-50%)",
        width: "calc(100% - 32px)",
        maxWidth: 398,
        background: "linear-gradient(135deg,#1A1430,#0D1A2E)",
        border: `1px solid rgba(201,168,76,0.35)`,
        borderRadius: 20,
        padding: "18px 18px 16px",
        zIndex: 200,
        boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        animation: "slideUp 0.4s ease",
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(30px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      {/* Close button */}
      <button
        onClick={dismiss}
        style={{ position: "absolute", top: 12, right: 12, background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8, width: 28, height: 28, cursor: "pointer", color: MUT, fontSize: 16, lineHeight: "28px" }}
      >×</button>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div style={{ width: 46, height: 46, borderRadius: 13, background: `linear-gradient(135deg,${G},#E8C96C)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>✈️</div>
        <div>
          <div style={{ color: TXT, fontSize: 15, fontWeight: 700 }}>Install MoneyPilot</div>
          <div style={{ color: MUT, fontSize: 12, marginTop: 2 }}>Add to your home screen for the best experience</div>
        </div>
      </div>

      {isIos ? (
        // iOS instructions
        <div>
          <div style={{ color: MUT, fontSize: 12, marginBottom: 10 }}>Follow these steps on your iPhone:</div>
          {[
            { icon: "⬆️", text: "Tap the Share button in Safari" },
            { icon: "➕", text: 'Scroll down and tap "Add to Home Screen"' },
            { icon: "✅", text: 'Tap "Add" to install MoneyPilot' },
          ].map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 18, width: 28, textAlign: "center", flexShrink: 0 }}>{s.icon}</div>
              <div style={{ color: TXT, fontSize: 13 }}>{s.text}</div>
            </div>
          ))}
          <button
            onClick={dismiss}
            style={{ width: "100%", marginTop: 10, background: `linear-gradient(135deg,${G},#E8C96C)`, border: "none", borderRadius: 12, padding: "12px", color: "#000", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}
          >Got it!</button>
        </div>
      ) : (
        // Android / Chrome install
        <div style={{ display: "flex", gap: 10 }}>

          <button
            onClick={install}
            disabled={installing}
            style={{ flex: 2, background: `linear-gradient(135deg,${G},#E8C96C)`, border: "none", borderRadius: 12, padding: "12px", color: "#000", fontWeight: 700, fontSize: 14, cursor: installing ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: installing ? 0.7 : 1 }}
          >{installing ? "Installing..." : "📲 Install App"}</button>
        </div>
      )}
    </div>
  );
}

function TopNav() {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: 430,
        background: "rgba(6,6,15,0.96)",
        borderBottom: `1px solid ${BDR}`,
        backdropFilter: "blur(20px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 60,
        padding: "0 18px",
        boxSizing: "border-box",
        zIndex: 60,
      }}
    >
      <div>
        <div style={{ color: MUT, fontSize: 12, marginBottom: 1 }}>Good day 👋</div>
        <div style={{ color: TXT, fontSize: 20, fontWeight: 800, letterSpacing: -0.5 }}>MoneyPilot</div>
      </div>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 13,
          background: `linear-gradient(135deg,${G},#E8C96C)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        ✈️
      </div>
    </div>
  );
}

function AuthScreen({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    const uname = username.trim().toLowerCase();
    if (!uname || !password) {
      setError("Please enter username and password.");
      return;
    }

    try {
      const users = JSON.parse(localStorage.getItem(LS_KEYS.users) || "[]");
      if (mode === "register") {
        if (users.some((u) => u.username === uname)) {
          setError("Username already exists.");
          return;
        }
        const newUser = { id: uid(), username: uname, password };
        localStorage.setItem(LS_KEYS.users, JSON.stringify([...users, newUser]));
        const allData = JSON.parse(localStorage.getItem(LS_KEYS.data) || "{}");
        allData[newUser.id] = { wallets: [], txns: [] };
        localStorage.setItem(LS_KEYS.data, JSON.stringify(allData));
        localStorage.setItem(LS_KEYS.session, newUser.id);
        onLogin(newUser);
        return;
      }

      const user = users.find((u) => u.username === uname && u.password === password);
      if (!user) {
        setError("Invalid username or password.");
        return;
      }
      localStorage.setItem(LS_KEYS.session, user.id);
      onLogin(user);
    } catch {
      setError("Could not access local data.");
    }
  };

  return (
    <div style={{ background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 18, fontFamily: "'DM Sans',sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 430, background: SFC, border: `1px solid ${BDR}`, borderRadius: 20, padding: 22 }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 42 }}>✈️</div>
          <div style={{ color: G, fontSize: 28, fontWeight: 800 }}>MoneyPilot</div>
          <div style={{ color: MUT, fontSize: 13, marginTop: 4 }}>Your data, your account, saved locally</div>
        </div>
        <div style={{ display: "flex", background: SFC2, borderRadius: 12, padding: 4, marginBottom: 14 }}>
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => {
                setMode(m);
                setError("");
              }}
              style={{
                flex: 1,
                border: "none",
                borderRadius: 10,
                padding: "10px 0",
                cursor: "pointer",
                background: mode === m ? G : "transparent",
                color: mode === m ? "#000" : MUT,
                fontWeight: 700,
                textTransform: "capitalize",
              }}
            >
              {m}
            </button>
          ))}
        </div>
        <Input label="Username" value={username} onChange={setUsername} placeholder="Enter username" />
        <Input label="Password" type="password" value={password} onChange={setPassword} placeholder="Enter password" />
        {error && <div style={{ color: RED, fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <Btn onClick={submit} style={{ width: "100%" }}>
          {mode === "login" ? "Login" : "Create Account"}
        </Btn>
        <div style={{ color: TXT, fontSize: 14, opacity: 0.8, textAlign: "center", marginTop: 24 }}>All Rights Reserved @Ravi Praveen</div>
      </div>

    </div>
  );
}

export default function App() {
  const [wallets, setWallets] = useState([]);
  const [txns, setTxns] = useState([]);
  const [screen, setScreen] = useState("dashboard");
  const [modal, setModal] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const users = JSON.parse(localStorage.getItem(LS_KEYS.users) || "[]");
      const sessionUserId = localStorage.getItem(LS_KEYS.session);
      const sessionUser = users.find((u) => u.id === sessionUserId) || null;
      setCurrentUser(sessionUser);

      if (sessionUser) {
        const allData = JSON.parse(localStorage.getItem(LS_KEYS.data) || "{}");
        const userData = allData[sessionUser.id] || { wallets: [], txns: [] };
        setWallets(userData.wallets || []);
        setTxns(userData.txns || []);
      } else {
        setWallets([]);
        setTxns([]);
      }
    } catch {
      setWallets([]);
      setTxns([]);
      setCurrentUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (!ready || !currentUser) return;
    try {
      const allData = JSON.parse(localStorage.getItem(LS_KEYS.data) || "{}");
      allData[currentUser.id] = { wallets, txns };
      localStorage.setItem(LS_KEYS.data, JSON.stringify(allData));
    } catch { }
  }, [wallets, txns, ready, currentUser]);

  const handleSaveTxn = (txn, isEdit = false) => {
    if (isEdit) {
      const old = txns.find((t) => t.id === txn.id);
      if (!old) return;

      // Reverse old transaction impact on wallet
      setWallets((prev) =>
        prev.map((w) => {
          if (w.id !== old.wId) return w;
          return { ...w, balance: w.balance + (old.type === "income" ? -old.amount : old.amount) };
        })
      );

      // Apply new transaction impact
      setTxns((prev) => prev.map((t) => (t.id === txn.id ? txn : t)));
      setWallets((prev) =>
        prev.map((w) => {
          if (w.id !== txn.wId) return w;
          return { ...w, balance: w.balance + (txn.type === "income" ? txn.amount : -txn.amount) };
        })
      );
    } else {
      setTxns((prev) => [txn, ...prev]);
      setWallets((prev) =>
        prev.map((w) => {
          if (w.id !== txn.wId) return w;
          return { ...w, balance: w.balance + (txn.type === "income" ? txn.amount : -txn.amount) };
        })
      );
    }
  };

  const handleDeleteTxn = (id) => {
    const txn = txns.find((t) => t.id === id);
    if (!txn) return;
    if (!window.confirm("Delete this transaction?")) return;
    setTxns((prev) => prev.filter((t) => t.id !== id));
    setWallets((prev) =>
      prev.map((w) => {
        if (w.id !== txn.wId) return w;
        return { ...w, balance: w.balance + (txn.type === "income" ? -txn.amount : txn.amount) };
      })
    );
    setModal(null);
  };

  const handleTransfer = ({ fromId, toId, amount }) => {
    const toW = wallets.find((w) => w.id === toId) || { name: "?" };
    setWallets((prev) =>
      prev.map((w) => {
        if (w.id === fromId) return { ...w, balance: w.balance - amount };
        if (w.id === toId) return { ...w, balance: w.balance + amount };
        return w;
      })
    );
    setTxns((prev) => [
      { id: uid(), wId: fromId, type: "transfer", amount, cat: "other", note: `Transfer → ${toW.name}`, date: new Date().toISOString() },
      ...prev,
    ]);
  };

  const handleChoose = (type) => {
    if (type === "transfer") setModal({ type: "transfer" });
    else setModal({ type: "txn", txnType: type });
  };

  if (!ready) {
    return (
      <div style={{ background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 14 }}>✈️</div>
          <div style={{ color: G, fontSize: 26, fontWeight: 800, letterSpacing: -0.5 }}>MoneyPilot</div>
          <div style={{ color: MUT, fontSize: 14, marginTop: 8 }}>Loading your finances...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
          *{box-sizing:border-box;margin:0;padding:0;}
          ::-webkit-scrollbar{width:0;height:0;}
          input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;}
          input[type=date]{color-scheme:dark;}
          input::placeholder{color:#444466;}
          body{background:#06060F;}
        `}</style>
        <InstallBanner />
        <AuthScreen onLogin={(user) => {
          setCurrentUser(user);
          try {
            const allData = JSON.parse(localStorage.getItem(LS_KEYS.data) || "{}");
            const userData = allData[user.id] || { wallets: [], txns: [] };
            setWallets(userData.wallets || []);
            setTxns(userData.txns || []);
          } catch {
            setWallets([]);
            setTxns([]);
          }
        }} />
      </>
    );
  }

  return (
    <div style={{ background: BG, minHeight: "100vh", maxWidth: 430, margin: "0 auto", fontFamily: "'DM Sans',sans-serif", color: TXT, position: "relative" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:0;height:0;}
        input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;}
        input[type=date]{color-scheme:dark;}
        input::placeholder{color:#444466;}
        body{background:#06060F;}
      `}</style>
      <TopNav />
      <InstallBanner />



      {screen === "dashboard" && <Dashboard wallets={wallets} txns={txns} setModal={setModal} />}
      {screen === "wallets" && (
        <Wallets
          wallets={wallets}
          txns={txns}
          setModal={setModal}
          onDeleteWallet={(id) => {
            setWallets((p) => p.filter((w) => w.id !== id));
            setTxns((p) => p.filter((t) => t.wId !== id));
          }}
        />
      )}
      {screen === "history" && <History wallets={wallets} txns={txns} onDelete={handleDeleteTxn} setModal={setModal} />}
      {screen === "reports" && <Reports txns={txns} />}

      <BottomNav screen={screen} setScreen={setScreen} setModal={setModal} />
      <AddChoiceSheet show={modal?.type === "addChoice"} onClose={() => setModal(null)} onChoose={handleChoose} />
      <AddTxnModal
        show={modal?.type === "txn"}
        onClose={() => setModal(null)}
        wallets={wallets}
        onSave={handleSaveTxn}
        initType={modal?.txnType || "expense"}
        editData={modal?.editData}
        onDelete={handleDeleteTxn}
      />
      <AddWalletModal show={modal?.type === "addWallet"} onClose={() => setModal(null)} onSave={(w) => setWallets((p) => [...p, w])} />
      <TransferModal show={modal?.type === "transfer"} onClose={() => setModal(null)} wallets={wallets} onSave={handleTransfer} />
    </div>
  );
}
